import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Fab, Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ServiceRequestModal from 'components/common/ServiceRequestModal';
import LogoIcon from '/Logo.svg';
import { typography } from 'theme';
import { useNavigate } from 'react-router-dom';
import { useSubmitSupportRequestMutation } from 'store/api/support.apislice';
import toast from 'react-hot-toast';
import { isNative } from 'utils/platform.utils';
import { containsHtmlTags, sanitizePlainTextInput } from 'utils/sanitize.utils';
import { getApiErrorMessage, isNetworkOrFetchError } from 'utils/api-error.utils';

// Component scope - Constants
const AUTO_CLOSE_DELAY = 2000; // 2 seconds
const MAX_QUERY_LENGTH = 300;
const MIN_QUERY_LENGTH = 10;
const OFFLINE_ERROR_MESSAGE = 'Network connection lost. Please try again.';
const SUBMIT_ERROR_FALLBACK = 'Something went wrong. Please try again.';
const HTML_NOT_ALLOWED_MESSAGE =
  'Special code or tags are not allowed. Please enter your query in plain text.';
const REASON_REQUIRED_MESSAGE = 'Please select a reason.';
const QUERY_REQUIRED_MESSAGE = 'Please enter your query.';
const QUERY_MIN_LENGTH_MESSAGE = 'Please enter at least 10 characters.';
const QUERY_MAX_LENGTH_MESSAGE = 'Maximum limit reached.';

//Support Reasons — must match the reason enum accepted by POST /api/support
const Support_Reasons = [
  "KYC QUERIES",
  "UNABLE TO PLACE ORDERS",
  "CLIENT REGISTRATION",
  "XSIP /STP/SWP REGISTRATION",
  "PASSWORD RESET",
  "UCC ACTIVATION",
  "AMC/SCHEME ISSUES",
  "MANDATE",
  "ENTRY UPDATION",
  "REFUND",
  "ALLOTMENT TRACKING",
  "PORTFOLIO TRACKING",
  "OTHERS"
];

// Component scope - Style functions that use theme
const getFABContainerSX = (theme) => ({
  zIndex: 9999,
  position: isNative() ? 'sticky' : 'fixed',
  bottom: theme?.spacing?.(3) ?? 24,
  right: theme?.spacing?.(0) ?? 0,
  marginRight: theme?.spacing?.(6) ?? 48,
  display: 'flex',
  justifyContent: 'flex-end',
  [theme?.breakpoints?.down?.('md') ?? '@media (max-width:900px)']: {
    bottom: theme?.spacing?.(8) ?? 64,
    marginRight: theme?.spacing?.(2) ?? 16,
  }
});

const getFABSX = (theme) => {
  // Use white color for FAB background (works in both light and dark modes)
  const bgColor = '#ffffff';

  return {
    bgcolor: bgColor,
    color: theme?.palette?.warning?.contrastText ?? '#000000',
    boxShadow: theme?.shadows?.[3] ?? 'none',
    '&:hover': {
      bgcolor: alpha(bgColor, 0.9),
      boxShadow: theme?.shadows?.[6] ?? 'none'
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: 60, md: 80 },
    height: { xs: 60, md: 80 },
    borderRadius: theme?.spacing?.(1) ?? 8,
    textTransform: 'none',
  };
};

const getLogoSX = (theme) => ({
  width: { xs: 24, md: 32 },
  height: { xs: 24, md: 32 },
  mb: theme?.spacing?.(0.5) ?? 4,
  objectFit: 'contain'
});

const getTextSX = (theme) => ({
  fontSize: {
    xs: '0.625rem', // 10px for mobile
    md: typography?.fontSize?.helperText ?? '0.75rem'
  },
  fontWeight: typography?.fontWeight?.semibold ?? 600,
  textAlign: 'center',
  color: '#000000',
});

/**
 * Count words in a text string
 * @param {string} text - Text to count words in
 * @returns {number} Word count
 */
const countWords = (text) => {
  const trimmed = text?.trim?.() ?? "";
  if ((trimmed?.length ?? 0) === 0) return 0;
  return trimmed?.split?.(/\s+/)?.filter?.(Boolean)?.length ?? 0;
};

/**
 * Normalize query input: strip leading spaces and cap consecutive spaces at 2
 * @param {string} value - Raw input value
 * @returns {string} Normalized value
 */
const normalizeQueryInput = (value) => {
  const sanitizedValue = sanitizePlainTextInput(value ?? "") ?? "";
  return sanitizedValue?.replace?.(/ {3,}/g, "  ") ?? "";
};

/**
 * Normalize query for API submit
 * @param {string} value - Raw query value
 * @returns {string} Normalized and trimmed value
 */
const normalizeQueryForSubmit = (value) => {
  return normalizeQueryInput(value)?.trim?.() ?? "";
};

/**
 * Count non-whitespace characters only
 * @param {string} value - Raw input value
 * @returns {number} Character count excluding spaces
 */
const getCharacterCount = (value) => {
  return value?.replace?.(/\s/g, "")?.length ?? 0;
};

/**
 * Cap input when non-space characters reach maxLength (spaces still allowed)
 * @param {string} value - Normalized input value
 * @param {number} maxLength - Max non-space character count
 * @returns {string} Capped value
 */
const capQueryByCharacterLimit = (value, maxLength) => {
  const safeValue = value ?? "";
  const limit = maxLength ?? 300;
  let nonSpaceCount = 0;
  let result = "";

  for (let i = 0; i < (safeValue?.length ?? 0); i += 1) {
    const char = safeValue?.[i] ?? "";
    const isSpace = /\s/.test(char);

    if (!isSpace) {
      if ((nonSpaceCount ?? 0) >= limit) {
        break;
      }
      nonSpaceCount += 1;
    }

    result += char;
  }

  return result ?? "";
};

/**
 * Service Request Container
 * Handles FAB button rendering, modal state, and form logic for service requests
 * @component
 */
const ServiceRequestContainer = ({
  disabled = false,
  contactrmModal = false,
  modalTitle = 'Need Help? Reach Out to us',
  onModalClose
}) => {
  // States, refs, selectors, RTK Query hooks
  const theme = useTheme();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState((contactrmModal ?? false) || false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [query, setQuery] = useState('');
  const isSubmittingRef = useRef(false);
  const [submitSupportRequest, { isLoading: isSubmittingRequest }] = useSubmitSupportRequestMutation();

  //use for the add the reason dropdown in the service request modal and also for the category in the support request submission
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [queryTouched, setQueryTouched] = useState(false);

  // Calculate word count and validation
  const wordCount = countWords(query ?? "");
  const trimmedQuery = query?.trim?.() ?? "";
  const charCount = getCharacterCount(query ?? "");
  const isReasonSelected = (reason?.trim?.() ?? "") !== "";
  const isQueryEmpty = (trimmedQuery?.length ?? 0) === 0;
  const isMinLengthMet = (charCount ?? 0) >= (MIN_QUERY_LENGTH ?? 10);
  const isAtMaxLength = (charCount ?? 0) >= (MAX_QUERY_LENGTH ?? 300);
  const isValid =
    !(isQueryEmpty ?? true) &&
    (isMinLengthMet ?? false) &&
    (charCount ?? 0) <= (MAX_QUERY_LENGTH ?? 300) &&
    (isReasonSelected ?? false);

  const shouldShowReasonError =
    ((reasonTouched ?? false) || (queryTouched ?? false)) && !(isReasonSelected ?? false);
  const reasonError = (shouldShowReasonError ?? false)
    ? (REASON_REQUIRED_MESSAGE ?? "Please select a reason.")
    : "";

  let queryError = "";
  if (queryTouched ?? false) {
    if (isQueryEmpty ?? true) {
      queryError = QUERY_REQUIRED_MESSAGE ?? "Please enter your query.";
    } else if (!(isMinLengthMet ?? false)) {
      queryError = QUERY_MIN_LENGTH_MESSAGE ?? "Please enter at least 10 characters.";
    } else if (isAtMaxLength ?? false) {
      queryError = QUERY_MAX_LENGTH_MESSAGE ?? "Maximum limit reached.";
    }
  }

  // Other functions and logic
  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    isSubmittingRef.current = false;
    setShowSuccess(false);
    setIsSubmitting(false);
    setModalOpen(false);
    setQuery('');
    setReason('');
    setReasonTouched(false);
    setQueryTouched(false);

    onModalClose?.();

    if ((contactrmModal ?? false) && !onModalClose) {
      navigate?.('/explore');
    }
  };

  const handleQueryChange = (e) => {
    setQueryTouched(true);
    const normalizedValue = normalizeQueryInput(e?.target?.value ?? "") ?? "";
    const cappedValue = capQueryByCharacterLimit(
      normalizedValue ?? "",
      MAX_QUERY_LENGTH ?? 300
    );

    setQuery(cappedValue ?? "");
  };

  const handleReasonChange = (e) => {
    setReasonTouched(true);
    setReason(e?.target?.value ?? "");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    // Block fast / double clicks immediately
    if ((isSubmittingRef?.current ?? false) || (isSubmitting ?? false) || (isSubmittingRequest ?? false)) {
      return;
    }

    // Validate before submitting
    if (!(isValid ?? false)) {
      return;
    }

    const normalizedRequest = normalizeQueryForSubmit(query ?? "") ?? "";

    if (containsHtmlTags(normalizedRequest ?? "")) {
      toast.error(HTML_NOT_ALLOWED_MESSAGE ?? "Special code or tags are not allowed. Please enter your query in plain text.");
      return;
    }

    if (typeof navigator !== 'undefined' && navigator?.onLine === false) {
      toast.error(OFFLINE_ERROR_MESSAGE ?? 'Network connection lost. Please try again.');
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      // Call the support API
      await submitSupportRequest({
        request: normalizedRequest ?? "",
        reason: reason ?? "",
        category: 'GENERAL'
      }).unwrap();
      // Show success message
      setShowSuccess(true);

      // Auto close after delay
      setTimeout(() => {
        handleClose?.();
      }, AUTO_CLOSE_DELAY ?? 2000);
    } catch (error) {
      console.error('Failed to submit service request:', error);

      const errorMessage = isNetworkOrFetchError(error ?? null)
        ? (OFFLINE_ERROR_MESSAGE ?? 'Network connection lost. Please try again.')
        : getApiErrorMessage(error ?? null, SUBMIT_ERROR_FALLBACK ?? 'Something went wrong. Please try again.');

      toast.error(errorMessage ?? OFFLINE_ERROR_MESSAGE ?? SUBMIT_ERROR_FALLBACK);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Return/rendering
  return (
    <>
      {!(contactrmModal ?? false) && (
        <Box sx={getFABContainerSX(theme)}>
          <Fab
            aria-label="Need help"
            onClick={handleOpen}
            disabled={disabled ?? false}
            sx={getFABSX(theme)}
          >
            <Box
              component="img"
              src={LogoIcon}
              alt="Help icon"
              sx={getLogoSX(theme)}
            />
            <Box sx={getTextSX(theme)}>Need Help?</Box>
          </Fab>
        </Box>
      )}

      <ServiceRequestModal
        open={modalOpen ?? false}
        onClose={handleClose}
        onSubmit={handleSubmit}
        loading={(isSubmitting ?? false) || (isSubmittingRequest ?? false)}
        showSuccess={showSuccess ?? false}
        query={query ?? ""}
        onQueryChange={handleQueryChange}
        reason={reason ?? ""}
        reasons={Support_Reasons ?? []}
        onReasonChange={handleReasonChange}
        reasonError={reasonError ?? ""}
        queryError={queryError ?? ""}
        disabled={!(isValid ?? false) || (isSubmitting ?? false) || (isSubmittingRequest ?? false)}
        minLength={MIN_QUERY_LENGTH ?? 10}
        maxLength={MAX_QUERY_LENGTH ?? 300}
        modalTitle={modalTitle ?? 'Need Help? Reach Out to us'}
      />
    </>
  );
};

ServiceRequestContainer.propTypes = {
  disabled: PropTypes.bool,
  contactrmModal: PropTypes.bool,
  modalTitle: PropTypes.string,
  onModalClose: PropTypes.func
};

export default ServiceRequestContainer;
